// frontend/src/components/community/MarketplaceTab.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Store, 
  Search, 
  Filter, 
  MapPin, 
  Star, 
  TrendingUp,
  Clock,
  ShoppingBag,
  CheckCircle,
  Briefcase,
  MessageSquare,
  Plus,
  ChevronRight,
  ChevronDown,
  X,
  Package,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useMarketplaceStore from '../../store/marketplaceStore';
import useAuthStore from '../../store/authStore';
import { BUSINESS_CATEGORIES } from '../../config/categories';
import ListBusinessModal from './ListBusinessModal';
import MessageBusinessModal from './MessageBusinessModal';
import OrderModal from './OrderModal';
import LocationPicker from '../common/LocationPicker';

const MarketplaceTab = ({ isFullPage = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Modal states
  const [showListModal, setShowListModal] = useState(false);
  const [selectedBusinessForMessage, setSelectedBusinessForMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedItemForOrder, setSelectedItemForOrder] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedBusinessForOrder, setSelectedBusinessForOrder] = useState(null);

  const { 
    searchBusinesses, 
    businesses: storeBusinesses,
    pagination,
    isLoading: storeIsLoading,
  } = useMarketplaceStore();

  // Load data on mount and when filters change
  useEffect(() => {
    loadDirectory(1);
  }, [selectedCategory, sortBy, selectedState, selectedCity]);

  const loadDirectory = async (page = 1) => {
    setIsLoading(true);
    try {
      const filters = {
        query: searchQuery || undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        page: page,
        limit: 50, // 50 items per page
        sortBy: sortBy === 'rating' ? 'rating' : sortBy === 'newest' ? 'createdAt' : 'name',
        state: selectedState || undefined,
        city: selectedCity || undefined
      };
      await searchBusinesses(filters);
      setCurrentPage(page);
      setHasLoadedOnce(true);
    } catch (error) {
      console.error('Failed to load businesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => loadDirectory(1);
  const handleClearSearch = () => {
    setSearchQuery('');
    loadDirectory(1);
  };

  const clearLocationFilters = () => {
    setSelectedState('');
    setSelectedCity('');
    setShowLocationFilter(false);
    loadDirectory(1);
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
    setShowMobileFilters(false);
    loadDirectory(1);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    loadDirectory(1);
  };

  const handleBusinessCreated = () => {
    loadDirectory(1);
    toast.success('Business listed successfully!');
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      TECHNOLOGY: '💻',
      MARKETING: '📈',
      DESIGN: '🎨',
      TELECOM: '📱',
      CONSULTING: '💼',
      CONTENT: '📷',
      SERVICES: '🛍️',
      PRODUCTS: '🏷️',
      JOBS: '💼'
    };
    return icons[category] || '🏢';
  };

  // Render stars
  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            className={`h-2.5 w-2.5 ${star <= Math.floor(rating) ? 'text-yellow-400 fill-current' : star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
        <span className="ml-0.5 text-[10px] text-gray-600">{rating.toFixed(1)}</span>
      </div>
    );
  };

  // ────────────── CATEGORIES ──────────────
  const allCategories = [
    { id: 'all', name: 'All', icon: '🎯' },
    ...BUSINESS_CATEGORIES.map(cat => ({ id: cat.id, name: cat.name, icon: cat.icon }))
  ];

  // ────────────── SORT OPTIONS ──────────────
  const sortOptions = [
    { value: 'rating', label: 'Rating' },
    { value: 'newest', label: 'Newest' },
    { value: 'name', label: 'Name' },
  ];

  // ────────────── PAGINATION CONTROLS ──────────────
  const PaginationControls = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const { page, totalPages, total } = pagination;
    const startItem = (page - 1) * 50 + 1;
    const endItem = Math.min(page * 50, total);

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-3 border-t border-primary-100/50">
        <div className="text-xs text-gray-500">
          Showing {startItem} - {endItem} of {total} businesses
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => loadDirectory(1)}
            disabled={page === 1}
            className="p-1.5 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            title="First page"
          >
            <ChevronsLeft className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={() => loadDirectory(page - 1)}
            disabled={page === 1}
            className="p-1.5 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600" />
          </button>
          
          <span className="px-3 py-1 text-xs font-medium text-gray-700 bg-primary-50 rounded-lg shadow-neomorph-inset min-w-[60px] text-center">
            {page} / {totalPages}
          </span>
          
          <button
            onClick={() => loadDirectory(page + 1)}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            title="Next page"
          >
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={() => loadDirectory(totalPages)}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            title="Last page"
          >
            <ChevronsRight className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>
    );
  };

  // ────────────── RENDER BUSINESS CARD ──────────────
  const renderBusinessCard = (business) => {
    const isVerified = business.verified === true;
    const coverSource = business.coverImageUrl;

    return (
      <div 
        key={business.id}
        className="bg-white rounded-xl shadow-neomorph overflow-hidden hover:shadow-neomorph-hover transition-all border border-primary-100/30 group"
      >
        {/* Cover Image - Smaller */}
        <div className="relative h-20 bg-gradient-to-r from-primary-600 to-primary-800">
          {coverSource ? (
            <img 
              src={coverSource} 
              alt={business.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Store className="h-6 w-6 text-white/30" />
            </div>
          )}
          
          {/* Verified Badge on Cover */}
          {isVerified && (
            <div className="absolute top-1 right-1 bg-green-600 text-white text-[6px] font-bold px-1.5 py-0.5 rounded shadow flex items-center gap-0.5">
              <CheckCircle className="h-2 w-2" />
              VERIFIED
            </div>
          )}
        </div>
        
        {/* Content - Compact */}
        <div className="p-2.5">
          <div className="flex items-start gap-2">
            {/* Logo - Smaller */}
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex-shrink-0 flex items-center justify-center overflow-hidden border-2 border-white -mt-4 relative z-10">
              {business.logoUrl ? (
                <img src={business.logoUrl} alt={business.name} className="h-8 w-8 rounded-lg object-cover" />
              ) : (
                <span className="text-sm">{getCategoryIcon(business.category)}</span>
              )}
            </div>
            
            {/* Business Info - Compact */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-0.5">
                <h4 className="font-semibold text-gray-800 text-xs truncate">{business.name}</h4>
                {isVerified && (
                  <CheckCircle className="h-2.5 w-2.5 text-green-600 flex-shrink-0" />
                )}
              </div>
              <div className="flex items-center text-[10px] text-gray-500">
                <MapPin className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
                <span className="truncate">{business.location || business.city || 'Location not set'}</span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                {renderStars(business.rating || 0)}
                <span className="text-[9px] text-gray-500">({business.reviewCount || 0})</span>
              </div>
            </div>
          </div>
          
          {/* Description - Compact */}
          {business.description && (
            <p className="text-[10px] text-gray-600 mt-1 line-clamp-1">{business.description}</p>
          )}
          
          {/* Tags - Compact */}
          {business.tags && business.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {business.tags.slice(0, 2).map((tag, idx) => (
                <span key={idx} className="px-1.5 py-0.5 bg-primary-50 text-primary-700 text-[8px] rounded border border-primary-200">
                  {tag}
                </span>
              ))}
              {business.tags.length > 2 && (
                <span className="px-1.5 py-0.5 bg-gray-50 text-gray-500 text-[8px] rounded">
                  +{business.tags.length - 2}
                </span>
              )}
            </div>
          )}
          
          {/* Actions - Compact */}
          <div className="grid grid-cols-2 gap-1 mt-2">
            <button 
              onClick={() => { 
                if (!user) { toast.error('Please login'); return; } 
                if (user?.id === business.userId) { toast('Your business'); return; } 
                setSelectedBusinessForMessage(business); 
                setShowMessageModal(true); 
              }} 
              className="px-1.5 py-1 bg-white text-gray-700 font-medium rounded shadow-neomorph hover:shadow-neomorph-hover transition-all text-[9px] flex items-center justify-center border border-gray-100"
            >
              <MessageSquare className="h-2.5 w-2.5 mr-0.5" />
              Chat
            </button>
            
            <button 
              onClick={() => navigate(`/community/marketplace/business/${business.id}`)} 
              className="px-1.5 py-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-[9px] flex items-center justify-center"
            >
              View
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ────────────── RENDER DIRECTORY ──────────────
  const renderDirectory = () => {
    const displayBusinesses = storeBusinesses || [];

    return (
      <div>
        {/* Search and Filters - Compact */}
        <div className="bg-white rounded-xl shadow-neomorph p-2.5 border border-primary-100/30 mb-2.5">
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-1.5">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input 
                  type="search" 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search businesses..." 
                  className="w-full pl-7 pr-7 py-1.5 bg-white rounded shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs" 
                />
                {searchQuery && (
                  <button 
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              
              <button 
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="px-2.5 py-1.5 bg-white text-gray-700 text-xs font-medium rounded shadow-neomorph hover:shadow-neomorph-hover flex items-center gap-1 border border-primary-100/30"
              >
                <Filter className="h-3.5 w-3.5 text-gray-400" />
                <span className="hidden sm:inline">{sortOptions.find(o => o.value === sortBy)?.label}</span>
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              </button>
              
              <button 
                onClick={() => setShowLocationFilter(!showLocationFilter)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded flex items-center gap-1 border ${
                  selectedState || selectedCity 
                    ? 'bg-primary-600 text-white border-primary-600 shadow-md shadow-primary-500/30' 
                    : 'bg-white text-gray-700 border-primary-100/30 shadow-neomorph hover:shadow-neomorph-hover'
                }`}
              >
                <MapPin className={`h-3.5 w-3.5 ${selectedState || selectedCity ? 'text-white' : 'text-gray-400'}`} />
                <span className="hidden sm:inline">
                  {selectedCity || selectedState || 'Location'}
                </span>
                {(selectedState || selectedCity) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearLocationFilters();
                    }}
                    className="ml-0.5 hover:bg-white/20 rounded-full p-0.5"
                  >
                    <X className={`h-2.5 w-2.5 ${selectedState || selectedCity ? 'text-white' : 'text-gray-400'}`} />
                  </button>
                )}
              </button>
              
              <button 
                onClick={() => setShowMobileFilters(true)}
                className="md:hidden px-2.5 py-1.5 bg-white text-gray-700 text-xs font-medium rounded shadow-neomorph hover:shadow-neomorph-hover flex items-center border border-primary-100/30"
              >
                <Filter className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </div>
            
            {/* Sort Dropdown */}
            {showSortDropdown && (
              <div className="absolute mt-8 right-0 z-10 w-36 bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSortChange(option.value)}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-primary-50 transition-all ${
                      sortBy === option.value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            
            {/* Location Filter */}
            {showLocationFilter && (
              <div className="bg-gray-50 rounded p-2 border border-gray-200 mt-1">
                <LocationPicker
                  selectedState={selectedState}
                  selectedCity={selectedCity}
                  onStateChange={(state) => {
                    setSelectedState(state);
                    setSelectedCity('');
                  }}
                  onCityChange={(city) => setSelectedCity(city)}
                  label="Filter by Location"
                  required={false}
                />
                <div className="flex gap-1.5 mt-2">
                  {(selectedState || selectedCity) && (
                    <button
                      onClick={clearLocationFilters}
                      className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded hover:bg-gray-300 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowLocationFilter(false);
                      loadDirectory(1);
                    }}
                    className="px-3 py-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs font-medium rounded shadow-md shadow-primary-500/30 hover:shadow-lg"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Categories - Compact */}
        <div className="bg-white rounded-xl shadow-neomorph p-1.5 border border-primary-100/30 mb-2.5 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {allCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="text-[10px]">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Active Filters - Compact */}
        {(selectedState || selectedCity || selectedCategory !== 'all' || searchQuery) && (
          <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 bg-white rounded shadow-neomorph border border-primary-100/30 mb-2.5">
            <span className="text-[10px] text-gray-500">Filters:</span>
            {selectedCategory !== 'all' && (
              <span className="inline-flex items-center px-2 py-0.5 bg-primary-50 text-primary-700 text-[10px] font-medium rounded border border-primary-200">
                {allCategories.find(c => c.id === selectedCategory)?.name || selectedCategory}
                <button onClick={() => handleCategoryChange('all')} className="ml-0.5 text-primary-600 hover:text-primary-800">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded border border-blue-200">
                "{searchQuery}"
                <button onClick={handleClearSearch} className="ml-0.5 text-blue-600 hover:text-blue-800">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )}
            {selectedState && (
              <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-medium rounded border border-green-200">
                <MapPin className="h-2.5 w-2.5 mr-0.5" />
                {selectedCity ? `${selectedCity}, ${selectedState}` : selectedState}
                <button onClick={clearLocationFilters} className="ml-0.5 text-green-600 hover:text-green-800">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )}
            {(selectedState || selectedCity || selectedCategory !== 'all' || searchQuery) && (
              <button 
                onClick={() => { 
                  setSelectedCategory('all'); 
                  setSearchQuery(''); 
                  clearLocationFilters(); 
                }} 
                className="text-[10px] text-red-600 hover:text-red-800 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
        )}
        
        {/* Business Grid - Compact */}
        <div className="bg-white rounded-xl shadow-neomorph p-2.5 border border-primary-100/30">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5" />
              <h3 className="text-sm font-semibold text-gray-800">
                {isFullPage ? 'All Businesses' : 'Directory'}
              </h3>
              {(selectedState || selectedCity) && (
                <span className="ml-1.5 text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                  {selectedCity || selectedState}
                </span>
              )}
            </div>
            <span className="text-[10px] text-gray-500">{pagination?.total || 0} businesses</span>
          </div>

          {isLoading || storeIsLoading ? (
            <div className="grid grid-cols-2 gap-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-50 rounded-xl overflow-hidden animate-pulse">
                  <div className="h-20 bg-gradient-to-br from-gray-200 to-gray-300"></div>
                  <div className="p-2.5">
                    <div className="flex items-start gap-2">
                      <div className="h-8 w-8 rounded-lg bg-gray-200 -mt-4"></div>
                      <div className="flex-1">
                        <div className="h-3 bg-gray-200 rounded w-3/4 mb-1"></div>
                        <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : displayBusinesses.length > 0 ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                {displayBusinesses.map(business => renderBusinessCard(business))}
              </div>
              
              {/* Pagination Controls */}
              <PaginationControls />
            </>
          ) : (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-50 shadow-neomorph-inset mb-2">
                <Store className="h-6 w-6 text-primary-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800 mb-0.5">No businesses found</h3>
              <p className="text-xs text-gray-500 mb-2">
                {(selectedState || selectedCity) ? 'Try adjusting your location filter' : (searchQuery ? 'Try a different search' : 'Be the first to list!')}
              </p>
              {(selectedState || selectedCity) && (
                <button onClick={clearLocationFilters} className="mr-1.5 px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200">
                  Clear Location Filter
                </button>
              )}
              <button onClick={() => setShowListModal(true)} className="px-3 py-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs font-medium rounded shadow-md shadow-primary-500/30 hover:shadow-lg">
                List Your Business
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ────────────── MOBILE FILTERS MODAL ──────────────
  const MobileFiltersModal = () => (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-neomorph max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-primary-100/50 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Filters & Sort</h3>
          <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronDown className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Categories</h4>
            <div className="grid grid-cols-2 gap-2">
              {allCategories.map((category) => (
                <button 
                  key={category.id} 
                  onClick={() => { setSelectedCategory(category.id); loadDirectory(1); setShowMobileFilters(false); }}
                  className={`flex items-center p-3 rounded-lg text-sm transition-all ${
                    selectedCategory === category.id 
                      ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                      : 'bg-gray-50 text-gray-600 border border-transparent'
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Location</h4>
            <div className="bg-gray-50 rounded-lg p-3">
              <LocationPicker
                selectedState={selectedState}
                selectedCity={selectedCity}
                onStateChange={(state) => {
                  setSelectedState(state);
                  setSelectedCity('');
                }}
                onCityChange={(city) => setSelectedCity(city)}
                label="Filter by Location"
                required={false}
              />
              {(selectedState || selectedCity) && (
                <button onClick={clearLocationFilters} className="mt-2 text-xs text-red-600 hover:text-red-800 flex items-center">
                  <X className="h-3 w-3 mr-1" /> Clear location filters
                </button>
              )}
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Sort By</h4>
            <div className="space-y-1">
              {sortOptions.map((option) => (
                <button 
                  key={option.value} 
                  onClick={() => { handleSortChange(option.value); setShowMobileFilters(false); }}
                  className={`w-full flex items-center px-4 py-3 rounded-lg text-sm ${
                    sortBy === option.value ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                  }`}
                >
                  {sortBy === option.value && <CheckCircle className="h-4 w-4 text-primary-600 mr-3" />}
                  <span className="flex-1 text-left">{option.label}</span>
                  {sortBy === option.value && <ChevronRight className="h-4 w-4 text-primary-600" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-2.5">
      {/* Main Content */}
      {renderDirectory()}

      {/* Mobile FAB for List Business */}
      <button 
        onClick={() => setShowListModal(true)} 
        className="md:hidden fixed bottom-20 right-4 p-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-full shadow-lg shadow-primary-500/40 z-20 active:scale-95 transition-transform duration-200"
      >
        <Plus className="h-5 w-5" />
      </button>

      {/* Modals */}
      {showMobileFilters && <MobileFiltersModal />}
      {showListModal && <ListBusinessModal onClose={() => setShowListModal(false)} onSuccess={handleBusinessCreated} />}
      {showMessageModal && selectedBusinessForMessage && (
        <MessageBusinessModal 
          business={selectedBusinessForMessage} 
          onClose={() => { setShowMessageModal(false); setSelectedBusinessForMessage(null); }} 
          onSuccess={() => toast.success('Message sent!')} 
        />
      )}
      {showOrderModal && selectedItemForOrder && selectedBusinessForOrder && (
        <OrderModal 
          item={selectedItemForOrder} 
          type={selectedItemType} 
          business={selectedBusinessForOrder} 
          onClose={() => { setShowOrderModal(false); setSelectedItemForOrder(null); setSelectedItemType(null); setSelectedBusinessForOrder(null); }} 
          onSuccess={() => toast.success('Order placed!')} 
        />
      )}

      <style jsx>{`
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default MarketplaceTab;